import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase';
import { PublicPage, Eyebrow } from './public/PublicLayout';

const CustomerRegistration = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [formData, setFormData] = useState({
        companyName: '',
        adminEmail: '',
        adminPassword: '',
        confirmPassword: '',
        contactName: '',
        phone: '',
        notes: ''
    });
    
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    
    // Check if we should show success screen (from URL param - survives re-renders!)
    const isSubmitted = searchParams.get('success') === 'true';
    const submittedEmail = searchParams.get('email') || '';

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.companyName.trim()) newErrors.companyName = 'Enter your company name.';
        if (!formData.adminEmail.trim()) {
            newErrors.adminEmail = 'Enter your email.';
        } else if (!/\S+@\S+\.\S+/.test(formData.adminEmail)) {
            newErrors.adminEmail = 'Enter a valid email address.';
        }
        if (!formData.adminPassword) {
            newErrors.adminPassword = 'Enter a password.';
        } else if (formData.adminPassword.length < 6) {
            newErrors.adminPassword = 'Password must be at least 6 characters.';
        }
        if (formData.adminPassword !== formData.confirmPassword) {
            newErrors.confirmPassword = "Passwords don't match.";
        }
        if (!formData.contactName.trim()) newErrors.contactName = 'Enter a contact name.';
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newErrors = validateForm();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        
        setIsSubmitting(true);
        const emailToSave = formData.adminEmail.toLowerCase().trim();
        
        try {
            // Step 1: Create Firebase Auth user
            const userCredential = await createUserWithEmailAndPassword(
                auth,
                emailToSave,
                formData.adminPassword
            );
            
            console.log('✅ Auth user created:', userCredential.user.uid);

            // Step 2: Call Cloud Function to create Firestore documents
            try {
                const createRegistration = httpsCallable(functions, 'createTenantRegistration');
                const result = await createRegistration({
                    companyName: formData.companyName.trim(),
                    contactName: formData.contactName.trim(),
                    phone: formData.phone.trim(),
                    notes: formData.notes.trim()
                });
                console.log('✅ Registration created:', result.data);
            } catch (funcError) {
                console.error('Cloud function error:', funcError);
                // Continue anyway - auth user is created
            }

            // Step 3: Sign out - user can't access until approved
            await signOut(auth);
            console.log('✅ User signed out');

            // Step 4: Navigate to success screen using URL params
            // This survives component re-mounts caused by auth state changes!
            navigate(`/register?success=true&email=${encodeURIComponent(emailToSave)}`, { replace: true });
            
        } catch (error) {
            console.error('Registration error:', error);
            
            if (error.code === 'auth/email-already-in-use') {
                setErrors({ adminEmail: 'This email is already registered. Use a different email or sign in.' });
            } else if (error.code === 'auth/weak-password') {
                setErrors({ adminPassword: 'Password is too weak. Choose a stronger one.' });
            } else if (error.code === 'auth/invalid-email') {
                setErrors({ adminEmail: 'Enter a valid email address.' });
            } else {
                setErrors({ submit: 'Registration failed. Try again.' });
            }
            
            // Make sure user is signed out on error
            try {
                await signOut(auth);
            } catch (signOutError) {
                // Ignore
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // --- Success Screen with Contact Info ---
    if (isSubmitted) {
        return (
            <PublicPage>
                <section className="px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
                    <div className="max-w-2xl mx-auto">
                        <Eyebrow>Carrier sign-up</Eyebrow>
                        <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold tracking-tight leading-tight text-white">
                            Account created
                            <span className="block pub-accent">Waiting for approval</span>
                        </h1>

                        <div className="pub-surface pub-line border rounded-2xl shadow-2xl shadow-black/30 p-8 sm:p-10 mt-8">
                            <div role="status" className="pub-warn px-4 py-3 rounded-lg flex items-start gap-3">
                                <i className="fas fa-clock mt-1" />
                                <p className="font-semibold">
                                    An admin will approve your account within 1–2 business days.
                                </p>
                            </div>

                            <h2 className="pub-text mt-8 text-xl font-bold">What happens next</h2>
                            <ul className="pub-muted mt-4 space-y-3">
                                <li className="flex items-start gap-3">
                                    <i className="fas fa-envelope pub-icon mt-1 w-5" />
                                    <span>We'll email <strong className="pub-text break-all">{submittedEmail}</strong> when your account is approved.</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <i className="fas fa-right-to-bracket pub-icon mt-1 w-5" />
                                    <span>Then sign in with your email and password.</span>
                                </li>
                            </ul>

                            <div className="pub-surface-2 pub-line border rounded-xl p-6 mt-8">
                                <h2 className="pub-text font-bold">Questions, or need it sooner?</h2>
                                <p className="pub-muted mt-1">Contact us:</p>
                                <div className="mt-3 space-y-2">
                                    <a
                                        href="mailto:admin@loadmemo.com"
                                        className="pub-link flex items-center gap-3 font-medium"
                                    >
                                        <i className="fas fa-envelope w-5" />
                                        <span>admin@loadmemo.com</span>
                                    </a>
                                    <a
                                        href="tel:+12015265176"
                                        className="pub-link flex items-center gap-3 font-medium"
                                    >
                                        <i className="fas fa-phone w-5" />
                                        <span>(201) 526-5176</span>
                                    </a>
                                </div>
                            </div>

                            <button
                                onClick={() => navigate('/')}
                                className="mt-8 w-full flex items-center justify-center gap-3 py-3.5 rounded-lg pub-btn font-semibold transition"
                            >
                                <i className="fas fa-house" />Back to home
                            </button>
                        </div>
                    </div>
                </section>
            </PublicPage>
        );
    }

    // --- Registration Form Screen ---
    return (
        <PublicPage>
            <section className="px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
                <div className="max-w-2xl mx-auto">
                    <Eyebrow>Carrier sign-up</Eyebrow>
                    <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold tracking-tight leading-tight text-white">
                        Create your carrier account
                    </h1>
                    <p className="mt-4 text-lg text-slate-200">
                        An admin approves new accounts before you can sign in.
                    </p>

                    <div className="pub-surface pub-line border rounded-2xl shadow-2xl shadow-black/30 p-8 sm:p-10 mt-8">
                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div className="grid sm:grid-cols-2 gap-5">
                                {[
                                    { name: 'companyName', label: 'Company name', type: 'text', placeholder: 'ABC Trucking Co.', icon: 'fas fa-building' },
                                    { name: 'contactName', label: 'Contact person', type: 'text', placeholder: 'John Smith', icon: 'fas fa-user' },
                                    { name: 'adminEmail', label: 'Admin email', type: 'email', placeholder: 'admin@company.com', icon: 'fas fa-envelope' },
                                    { name: 'phone', label: 'Phone (optional)', type: 'tel', placeholder: '(555) 123-4567', icon: 'fas fa-phone' },
                                    { name: 'adminPassword', label: 'Password', type: 'password', placeholder: 'At least 6 characters', icon: 'fas fa-lock' },
                                    { name: 'confirmPassword', label: 'Confirm password', type: 'password', placeholder: '••••••••', icon: 'fas fa-lock' },
                                ].map(field => (
                                    <div key={field.name}>
                                        <label htmlFor={field.name} className="pub-text block font-semibold mb-2">{field.label}</label>
                                        <div className="relative">
                                            <i className={`${field.icon} pub-faint absolute left-4 top-1/2 -translate-y-1/2`} />
                                            <input id={field.name} name={field.name} type={field.type} value={formData[field.name]} onChange={handleChange}
                                                aria-invalid={errors[field.name] ? true : undefined}
                                                aria-describedby={errors[field.name] ? `${field.name}-error` : undefined}
                                                className="pub-input w-full py-3 pl-12 pr-4 rounded-lg transition"
                                                placeholder={field.placeholder} />
                                        </div>
                                        {errors[field.name] && <p id={`${field.name}-error`} className="pub-error mt-2 px-3 py-2 rounded-md text-sm font-semibold">{errors[field.name]}</p>}
                                    </div>
                                ))}
                            </div>
                            <div>
                                <label htmlFor="notes" className="pub-text block font-semibold mb-2">Notes (optional)</label>
                                <textarea id="notes" name="notes" rows={3} value={formData.notes} onChange={handleChange} placeholder="Anything we should know"
                                    className="pub-input w-full py-3 px-4 rounded-lg transition" />
                            </div>
                            {errors.submit && <div role="alert" className="pub-error px-4 py-3 rounded-lg text-sm font-semibold">{errors.submit}</div>}
                            <button type="submit" disabled={isSubmitting} className="w-full flex items-center justify-center gap-3 py-3.5 rounded-lg pub-btn text-lg font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed">
                                {isSubmitting
                                    ? <><i className="fas fa-spinner fa-spin" />Creating account…</>
                                    : <>Create account <i className="fas fa-arrow-right" /></>}
                            </button>
                        </form>
                        <p className="pub-line pub-muted mt-7 pt-5 border-t text-center">
                            Already have an account?{' '}
                            <button type="button" onClick={() => navigate('/')} className="pub-link font-semibold">Sign in</button>
                        </p>
                    </div>
                </div>
            </section>
        </PublicPage>
    );
};

export default CustomerRegistration;